/**
 * The Terminal tab, from the page's side — desktop only. See desktop/src-tauri/src/terminal.rs.
 *
 * A command runs in a folder the person opened, with the system shell (PowerShell or cmd on
 * Windows, the login shell on Linux and macOS); the command travels on the shell's stdin. In a
 * browser there is no shell to reach: `terminalShell` answers null and `runTerminal` throws
 * `not-available`, and the panel says the terminal needs the desktop app.
 *
 * The pure parts — command history, colour codes, run ids — are here so they can be tested.
 */

import { isDesktop } from './desktop'

export interface TerminalShell {
  /** "powershell", "cmd", "bash", "zsh", "sh"… */
  id: string
  /** What the panel shows: "PowerShell", "cmd", "zsh"… */
  name: string
  program: string
}

export type TerminalStream = 'stdout' | 'stderr'

export interface TerminalHandlers {
  onLine: (line: string, stream: TerminalStream) => void
  onExit: (code: number | null) => void
}

/** The longest command the desktop accepts (terminal.rs). */
export const MAX_COMMAND = 32 * 1024

async function invoke<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke: call } = await import('@tauri-apps/api/core')
  return call<T>(command, args)
}

export function terminalAvailable(): boolean {
  return isDesktop()
}

/** Which shell runs the commands here, or null in a browser. */
export async function terminalShell(): Promise<TerminalShell | null> {
  if (!isDesktop()) return null
  return invoke<TerminalShell>('terminal_shell')
}

/**
 * Runs `command` in `folder`. Resolves when it has started; `handlers` hear its output and its end.
 * Returns the way to stop it (and what it started).
 */
export async function runTerminal(runId: string, folder: string, command: string, handlers: TerminalHandlers): Promise<() => Promise<void>> {
  if (!isDesktop()) throw new Error('not-available')
  const { listen } = await import('@tauri-apps/api/event')
  const stopOutput = await listen<{ run_id: string; stream: TerminalStream; line: string }>('devpulse://terminal-output', (event) => {
    if (event.payload.run_id === runId) handlers.onLine(event.payload.line, event.payload.stream)
  })
  const stopExit = await listen<{ run_id: string; code: number | null }>('devpulse://terminal-exit', (event) => {
    if (event.payload.run_id !== runId) return
    stopOutput()
    stopExit()
    handlers.onExit(event.payload.code)
  })

  try {
    await invoke('terminal_run', { runId, folder, command })
  } catch (error) {
    stopOutput()
    stopExit()
    throw error
  }
  return () => invoke('terminal_stop', { runId })
}

// ------------------------------------------------------------------------------ pure helpers

/** A run id the desktop accepts: letters, digits and dashes. */
export function newRunId(): string {
  const random = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  return `term-${random}`
}

/** Colour and cursor codes a command printed for a real terminal, taken out for the page. */
export function stripAnsi(text: string): string {
  // CSI sequences (colours, cursor moves), OSC sequences (titles, links), then lone escapes.
  // eslint-disable-next-line no-control-regex
  return text.replace(/\u001b\[[0-?]*[ -/]*[@-~]/g, '').replace(/\u001b\][^\u0007\u001b]*(?:\u0007|\u001b\\)/g, '').replace(/\u001b[@-_]/g, '')
}

/** Whether the page should send it: something to run, not over the desktop's limit. */
export function canRun(command: string): boolean {
  return command.trim().length > 0 && command.length <= MAX_COMMAND
}

/** The history after running `command`: last at the end, no immediate repeat, at most `max`. */
export function pushHistory(history: string[], command: string, max = 200): string[] {
  const entry = command.trim()
  if (!entry) return history
  const next = history[history.length - 1] === entry ? history : [...history, entry]
  return next.length > max ? next.slice(next.length - max) : next
}

/**
 * ↑ / ↓ through the history, as a shell does. `index` is where the person is: `history.length`
 * means "the line being typed" (`draft`), which ↓ past the newest entry brings back.
 */
export function navigateHistory(
  history: string[],
  index: number,
  direction: 'up' | 'down',
  draft: string,
): { index: number; value: string } {
  if (history.length === 0) return { index: 0, value: draft }
  const current = Math.min(Math.max(index, 0), history.length)
  const next = direction === 'up' ? Math.max(current - 1, 0) : Math.min(current + 1, history.length)
  return { index: next, value: next === history.length ? draft : history[next]! }
}

/** Keeps the output to the last `max` lines, so a chatty command does not grow the page forever. */
export function capLines<T>(lines: T[], max = 5000): T[] {
  return lines.length > max ? lines.slice(lines.length - max) : lines
}
